/* ==========================================================================
   REMRY — v3 · mejoras de la ficha

   Tres comportamientos pequeños, todos opcionales: sin este archivo la
   página se lee igual (los paneles van seguidos, la entrega dice el plazo,
   la galería se desliza sin contador).

     - [data-tabs]      los paneles del documento de ejemplo pasan a pestañas;
                        con [data-live], entran renglón a renglón al verse y
                        avanzan solas hasta que alguien toca una
     - [data-wave]      la onda de la grabación se mueve solo a la vista
     - [data-delivery]  «3 a 5 días hábiles» pasa a fechas concretas
     - [data-gallery]   el contador sigue a la imagen visible
   ========================================================================== */

(() => {
  /* --- Pestañas ----------------------------------------------------------- */

  document.querySelectorAll('[data-tabs]').forEach((root) => {
    const list = root.querySelector('[data-tablist]');
    const tabs = Array.from(root.querySelectorAll('[data-tab]'));
    const panels = tabs.map((tab) => root.querySelector(`#${CSS.escape(tab.dataset.tab)}`));
    if (!list || !tabs.length || panels.some((panel) => !panel)) return;

    list.setAttribute('role', 'tablist');

    const select = (index, focus) => {
      tabs.forEach((tab, i) => {
        const on = i === index;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
        panels[i].hidden = !on;
      });
      if (focus) tabs[index].focus();
    };

    tabs.forEach((tab, i) => {
      tab.setAttribute('role', 'tab');
      tab.id = `tab-${tab.dataset.tab}`;
      tab.setAttribute('aria-controls', tab.dataset.tab);
      panels[i].setAttribute('role', 'tabpanel');
      panels[i].setAttribute('aria-labelledby', tab.id);

      tab.addEventListener('click', () => {
        select(i);
        try {
          window.Shopify?.analytics?.publish('remry_demo_tab', { tab: tab.textContent.trim() });
        } catch {
          /* La analítica nunca rompe la interfaz. */
        }
      });

      tab.addEventListener('keydown', (event) => {
        const move = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
        if (!move) return;
        event.preventDefault();
        select((i + move + tabs.length) % tabs.length, true);
      });
    });

    root.setAttribute('data-tabs-ready', '');
    select(0);

    /* La demostración en marcha. Al llegar a la vista, cada pestaña entra
       renglón a renglón y, mientras nadie toque nada, pasa sola a la
       siguiente: quien solo desliza ve las cuatro salidas sin pulsar. Se
       para en la última, al primer toque o al pasar el ratón, y se pausa
       fuera de la vista. */
    if (!root.hasAttribute('data-live') || !('IntersectionObserver' in window)) {
      root.classList.add('is-live');
      return;
    }

    const STEP = 7000;
    let current = 0;
    let timer = 0;
    let auto = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    root.style.setProperty('--r4-step', `${STEP}ms`);

    const stop = () => {
      auto = false;
      clearTimeout(timer);
      root.removeAttribute('data-autoplay');
    };

    const schedule = () => {
      clearTimeout(timer);
      if (!auto) return;
      root.setAttribute('data-autoplay', '');
      timer = setTimeout(() => {
        if (!auto) return;
        if (current + 1 >= tabs.length) {
          stop();
          return;
        }
        current += 1;
        select(current);
        schedule();
      }, STEP);
    };

    tabs.forEach((tab, i) => tab.addEventListener('click', () => {
      current = i;
      stop();
    }));
    root.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') stop();
    });
    root.addEventListener('focusin', stop);

    new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          root.classList.add('is-live');
          schedule();
        } else {
          clearTimeout(timer);
          root.removeAttribute('data-autoplay');
        }
      },
      { threshold: 0.4 }
    ).observe(root);
  });

  /* --- La onda de la grabación ---------------------------------------------- */

  document.querySelectorAll('[data-wave]').forEach((el) => {
    if (!('IntersectionObserver' in window)) return;
    new IntersectionObserver(([entry]) => {
      el.classList.toggle('is-live', entry.isIntersecting);
    }).observe(el);
  });

  /* --- Fecha de entrega ----------------------------------------------------
     La política promete un plazo en días hábiles. Una fecha se entiende
     mejor que un plazo, así que se cuenta desde hoy saltando sábados y
     domingos. Es la misma promesa, dicha en fechas; los festivos no se
     descuentan, por eso el texto dice «entre». */

  const addBusinessDays = (from, days) => {
    const date = new Date(from);
    let left = days;
    while (left > 0) {
      date.setDate(date.getDate() + 1);
      const weekday = date.getDay();
      if (weekday !== 0 && weekday !== 6) left -= 1;
    }
    return date;
  };

  const monthOf = (date) => new Intl.DateTimeFormat('es-ES', { month: 'long' }).format(date);

  document.querySelectorAll('[data-delivery]').forEach((el) => {
    const min = Number(el.dataset.minDays);
    const max = Number(el.dataset.maxDays);
    if (!min || !max) return;

    try {
      const today = new Date();
      const first = addBusinessDays(today, min);
      const last = addBusinessDays(today, max);
      // Una línea: «del 7 al 9 de octubre». Si el plazo cruza de mes,
      // cada día lleva el suyo.
      const range =
        first.getMonth() === last.getMonth()
          ? `del ${first.getDate()} al ${last.getDate()} de ${monthOf(last)}`
          : `del ${first.getDate()} de ${monthOf(first)} al ${last.getDate()} de ${monthOf(last)}`;
      el.textContent = `Envío gratis. Llega ${range}.`;
    } catch {
      /* Sin Intl se queda el plazo que escribió el servidor. */
    }
  });

  /* --- Contador de la galería ------------------------------------------------
     La tira tiene diapositivas de anchos distintos, así que «cuál se ve» se
     decide por posición: la última cuyo borde izquierdo ya ha llegado al
     borde de la tira. Con un observador de intersección, la segunda imagen
     (que asoma) contaba como visible y el contador arrancaba en 2. */

  document.querySelectorAll('[data-gallery]').forEach((rail) => {
    const index = rail.parentElement?.querySelector('[data-gallery-index]');
    const items = Array.from(rail.children);
    const stage = rail.closest('.r4-stage');
    if (!index || items.length < 2) return;

    let queued = false;
    const paint = () => {
      queued = false;
      const edge = rail.getBoundingClientRect().left + 24;
      let current = 0;
      items.forEach((item, i) => {
        if (item.getBoundingClientRect().left <= edge) current = i;
      });
      // Al final de la tira la última diapositiva nunca llega al borde.
      const scrollable = rail.scrollWidth > rail.clientWidth + 4;
      if (scrollable && rail.scrollLeft > 0 && rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4) {
        current = items.length - 1;
      }
      index.textContent = String(current + 1);
      stage?.toggleAttribute('data-slid', current > 0);
    };

    rail.addEventListener(
      'scroll',
      () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(paint);
      },
      { passive: true }
    );

    paint();
  });
})();
